import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Box,
  CheckCircle2,
  Clock3,
  FileCheck2,
  Gift,
  LineChart,
  Package,
  Percent,
  Sparkles,
  Star,
  Ticket,
  Wallet,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { formatPts, pick } from '../../utils/moduleHelpers';
import ClaimRewardModal from './ClaimRewardModal';
import '../../styles/module-shared.css';
import './rewards-module.css';

const FALLBACK_CATEGORIES = [
  'All Rewards',
  'Cash Rewards',
  'Trading Credits',
  'Discounts',
  'Merchandise',
  'Shopping Vouchers',
];

const THEMES = [
  { key: 'green', Icon: Wallet },
  { key: 'purple', Icon: Ticket },
  { key: 'orange', Icon: Package },
  { key: 'blue', Icon: LineChart },
  { key: 'rose', Icon: Box },
  { key: 'amber', Icon: Percent },
  { key: 'teal', Icon: Gift },
  { key: 'indigo', Icon: Sparkles },
];

const guessTheme = (reward, index) => {
  const text = `${reward.rewardName || ''} ${reward.category || ''} ${reward.description || ''}`.toLowerCase();
  if (/cash|wallet|money|usd|\$/.test(text)) return THEMES[0];
  if (/voucher|gift|amazon|card|ticket/.test(text)) return THEMES[1];
  if (/merch|box|package|swag|shirt/.test(text)) return THEMES[2];
  if (/trad|lot|credit|chart/.test(text)) return THEMES[3];
  if (/discount|percent|%|off/.test(text)) return THEMES[5];
  return THEMES[index % THEMES.length];
};

const RewardCardVisual = ({ reward, index, popular }) => {
  const theme = guessTheme(reward, index);
  const Icon = theme.Icon;
  const imageUrl = String(reward.imageUrl || reward.rewardPicture || '').trim();
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imageUrl]);

  const showPhoto = Boolean(imageUrl) && !imgFailed;

  return (
    <div className={`rc-card__media${showPhoto ? ' has-photo' : ''}`}>
      {popular ? <span className="rc-card__badge">Most Popular</span> : null}
      {showPhoto ? (
        <img
          className="rc-card__photo"
          src={imageUrl}
          alt={reward.rewardName || reward.name || 'Reward'}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div className="rc-card__icon">
          <Icon className="w-7 h-7" strokeWidth={1.75} />
        </div>
      )}
    </div>
  );
};

const usdFromPoints = (pts) => {
  const n = Number(pts) || 0;
  return (n / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const ClaimRewards = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rewards, setRewards] = useState([]);
  const [summary, setSummary] = useState({});
  const [category, setCategory] = useState('All Rewards');
  const [sortBy, setSortBy] = useState('recommended');
  const [selected, setSelected] = useState(null);
  const [claimType, setClaimType] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const bundle = await apiService.buildRewardsBundle();
      if (cancelled()) return;

      if (!bundle.listRes?.success && bundle.listRes?.error) {
        setError(bundle.listRes.error);
      }

      setRewards(bundle.catalog || []);
      setSummary(bundle.summary || {});
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('rewards-claim-claimed-name-v3', fetchData, true);

  const totalPoints = Number(
    pick(summary, 'totalLoyaltyPoints', 'totalPointsEarned', 'totalLots') ?? 0
  );
  const availableToClaim = Number(
    pick(summary, 'availableLoyaltyPoints', 'availablePoints', 'pointsToSpend') ?? 0
  );
  const pendingCount = Number(pick(summary, 'pendingCount') ?? 0);
  const claimedCount = Number(pick(summary, 'claimedCount') ?? 0);
  const availableCount = rewards.filter(
    (r) => r.isAvailable || String(r.status || '').toLowerCase() === 'available'
  ).length;

  const categories = useMemo(() => {
    const fromApi = new Set();
    rewards.forEach((r) => {
      const c = r.category || r.rewardCategory || r.type;
      if (c && String(c).toLowerCase() !== 'milestone') fromApi.add(String(c));
    });
    if (fromApi.size === 0) return FALLBACK_CATEGORIES;
    return ['All Rewards', ...Array.from(fromApi)];
  }, [rewards]);

  const filtered = useMemo(() => {
    let list = [...rewards];
    if (category !== 'All Rewards' && category !== 'All') {
      const key = category.toLowerCase();
      list = list.filter((r) => {
        const blob = `${r.category || ''} ${r.rewardCategory || ''} ${r.type || ''} ${r.rewardName || ''} ${r.description || ''}`.toLowerCase();
        if (key.includes('cash')) return /cash|wallet|money|usd|\$/.test(blob);
        if (key.includes('trading')) return /trad|lot|credit/.test(blob);
        if (key.includes('discount')) return /discount|percent|%|off/.test(blob);
        if (key.includes('merchandise')) return /merch|box|package|swag|shirt/.test(blob);
        if (key.includes('voucher') || key.includes('shopping')) {
          return /voucher|gift|amazon|card|shop/.test(blob);
        }
        return String(r.category || r.rewardCategory || r.type || '').toLowerCase() === key;
      });
    }

    list.sort((a, b) => {
      const pa = Number(a.requiredLots ?? a.pointsCost ?? a.requiredPoints ?? a.points ?? 0);
      const pb = Number(b.requiredLots ?? b.pointsCost ?? b.requiredPoints ?? b.points ?? 0);
      const aAvail = a.isAvailable ? 0 : a.isClaimed ? 2 : 1;
      const bAvail = b.isAvailable ? 0 : b.isClaimed ? 2 : 1;
      if (sortBy === 'points-asc') return pa - pb;
      if (sortBy === 'points-desc') return pb - pa;
      if (sortBy === 'name') {
        return String(a.rewardName || a.name || '').localeCompare(String(b.rewardName || b.name || ''));
      }
      // recommended: available first, then popular, then cost
      if (aAvail !== bAvail) return aAvail - bAvail;
      const pop = Number(Boolean(b.isPopular)) - Number(Boolean(a.isPopular));
      if (pop) return pop;
      return pa - pb;
    });
    return list;
  }, [rewards, category, sortBy]);

  const getStatus = (reward) => {
    if (
      reward.isClaimed ||
      reward.claimed ||
      reward.alreadyClaimed ||
      String(reward.status || '').toLowerCase() === 'claimed'
    ) {
      return 'claimed';
    }
    const cost = Number(
      reward.requiredPoints ?? reward.pointsCost ?? reward.requiredLots ?? reward.points ?? 0
    );
    const balance = Number(
      reward.availableWalletPoints ?? availableToClaim ?? 0
    );
    if (cost > 0 && balance < cost) return 'locked';
    if (reward.isAvailable || reward.canAfford || String(reward.status || '').toLowerCase() === 'available') {
      return 'available';
    }
    if (cost <= 0 || balance >= cost) return 'available';
    return 'locked';
  };

  const handleConfirmClaim = async (payload = {}) => {
    if (!selected) return;
    if (getStatus(selected) === 'claimed' || selected.isClaimed || selected.alreadyClaimed) {
      setClaimError('This reward is already claimed.');
      return;
    }

    const selectedClaimType = payload.claimType || claimType || 'cash';

    setClaimLoading(true);
    setClaimError('');
    try {
      const cost = Number(
        selected.requiredPoints ?? selected.pointsCost ?? selected.requiredLots ?? selected.points ?? 0
      );
      const balance = Number(selected.availableWalletPoints ?? availableToClaim ?? 0);
      if (cost > 0 && balance < cost) {
        setClaimError(
          `Not enough points. This reward needs ${formatPts(cost)} PTS, but you only have ${formatPts(balance)} available.`
        );
        return;
      }

      const rewardId = selected.affiliateRewardId ?? selected.rewardId ?? selected.id;
      const rewardName = selected.rewardName || selected.name || '';
      const res = await apiService.claimReward(rewardId, selectedClaimType);
      if (!res.success) {
        const apiRequired = res.data?.requiredPoints ?? res.data?.RequiredPoints;
        const apiAvailable = res.data?.availablePoints ?? res.data?.AvailablePoints;
        if (apiRequired != null && apiAvailable != null) {
          setClaimError(
            res.error ||
              `Insufficient available points. Required: ${formatPts(apiRequired)}, Available: ${formatPts(apiAvailable)}.`
          );
        } else {
          setClaimError(res.error || res.message || 'Claim failed');
        }
        return;
      }

      // Immediate UI update — disable button + show Already Claimed
      setRewards((prev) =>
        prev.map((r) => {
          const sameId =
            String(r.affiliateRewardId ?? r.id ?? '') === String(rewardId ?? '');
          const sameName =
            rewardName &&
            String(r.rewardName || r.name || '')
              .trim()
              .toLowerCase() === String(rewardName).trim().toLowerCase();
          if (!sameId && !sameName) return r;
          return {
            ...r,
            isClaimed: true,
            claimed: true,
            alreadyClaimed: true,
            isAvailable: false,
            canAfford: false,
            status: 'claimed',
          };
        })
      );

      setSelected(null);
      setClaimType('');
      await fetchData({ cancelled: () => false });
    } catch (err) {
      setClaimError(toUserMessage(err, ERROR_MESSAGES.request));
    } finally {
      setClaimLoading(false);
    }
  };

  const scrollToCatalog = () => {
    document.getElementById('rewards-catalog-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="page rd-page rc-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Gift}
          title="Rewards"
          subtitle="Earn points, unlock rewards, and enjoy exclusive benefits."
          iconBg="linear-gradient(135deg,#6b0000,#8b0000)"
        />
        <ModuleAlert message={error} />

        {loading ? (
          <div className="module-card">
            <AppLoader color="#8b0000" />
          </div>
        ) : (
          <>
            <section className="rc-hero">
              <div className="rc-hero__copy">
                <span className="rc-hero__eyebrow">Rewards Catalog</span>
                <h2>Turn points into real rewards</h2>
                <p>
                  Browse cash back, trading credits, vouchers, and more — claim instantly with your
                  loyalty points.
                </p>
              </div>
              <div className="rc-hero__balance">
                <strong>{formatPts(availableToClaim)}</strong>
                <span>Available to claim</span>
              </div>
            </section>

            <section className="rc-stats">
              <article className="rc-stat rc-stat--amber">
                <div className="rc-stat__icon">
                  <Star className="w-5 h-5" />
                </div>
                <div className="rc-stat__label">Total Points</div>
                <div className="rc-stat__value">{formatPts(totalPoints)}</div>
                <div className="rc-stat__sub">USD {usdFromPoints(totalPoints).replace('$', '')}</div>
                <div className="rc-stat__footer">
                  <span className="rc-stat__footer-spacer" aria-hidden="true" />
                </div>
              </article>

              <article className="rc-stat rc-stat--green">
                <div className="rc-stat__icon">
                  <Gift className="w-5 h-5" />
                </div>
                <div className="rc-stat__label">Available to Claim</div>
                <div className="rc-stat__value">{formatPts(availableToClaim)}</div>
                <div className="rc-stat__sub">USD {usdFromPoints(availableToClaim).replace('$', '')}</div>
                <div className="rc-stat__footer">
                  <button type="button" className="rc-stat__btn" onClick={scrollToCatalog}>
                    Claim Now
                  </button>
                </div>
              </article>

              <article className="rc-stat rc-stat--orange">
                <div className="rc-stat__icon">
                  <Clock3 className="w-5 h-5" />
                </div>
                <div className="rc-stat__label">Pending</div>
                <div className="rc-stat__value">{formatPts(pendingCount)}</div>
                <div className="rc-stat__sub">USD {usdFromPoints(pendingCount).replace('$', '')}</div>
                <div className="rc-stat__footer">
                  <span className="rc-stat__footer-spacer" aria-hidden="true" />
                </div>
              </article>

              <article className="rc-stat rc-stat--teal">
                <div className="rc-stat__icon">
                  <FileCheck2 className="w-5 h-5" />
                </div>
                <div className="rc-stat__label">Claimed Rewards</div>
                <div className="rc-stat__value">{formatPts(claimedCount)}</div>
                <div className="rc-stat__sub">
                  {availableCount > 0
                    ? `${availableCount} ready now`
                    : `USD ${usdFromPoints(claimedCount).replace('$', '')}`}
                </div>
                <div className="rc-stat__footer">
                  <Link className="rc-stat__link" to="/rewards/report">
                    View History
                  </Link>
                </div>
              </article>
            </section>

            <div className="rc-toolbar">
              <div className="rc-tabs" role="tablist" aria-label="Reward categories">
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="tab"
                    aria-selected={category === c}
                    className={`rc-tab${category === c ? ' is-active' : ''}`}
                    onClick={() => setCategory(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <label className="rc-sort">
                <span>Sort by:</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort rewards">
                  <option value="recommended">Recommended</option>
                  <option value="points-asc">Points: Low to High</option>
                  <option value="points-desc">Points: High to Low</option>
                  <option value="name">Name</option>
                </select>
              </label>
            </div>

            {filtered.length === 0 ? (
              <div id="rewards-catalog-grid" className="module-card rc-empty">
                <div className="rc-empty__icon" aria-hidden="true">
                  <Gift className="w-8 h-8" strokeWidth={1.75} />
                </div>
                <h3 className="rc-empty__title">No rewards found</h3>
                <p className="rc-empty__text">
                  {category === 'All Rewards'
                    ? 'There are no rewards available in the catalog right now. Check back soon.'
                    : `No rewards found in “${category}”. Try another category or view all rewards.`}
                </p>
                {category !== 'All Rewards' ? (
                  <button type="button" className="rc-empty__btn" onClick={() => setCategory('All Rewards')}>
                    View all rewards
                  </button>
                ) : null}
              </div>
            ) : (
              <div id="rewards-catalog-grid" className="rc-grid">
                {filtered.map((reward, index) => {
                  const status = getStatus(reward);
                  const cost = Number(reward.requiredLots ?? reward.pointsCost ?? reward.requiredPoints ?? 0);
                  const popular = Boolean(reward.isPopular || reward.mostPopular) || index < 4;
                  const theme = guessTheme(reward, index);
                  return (
                    <article
                      key={reward.affiliateRewardId || reward.rewardId || reward.id || index}
                      className={`rc-card theme-${theme.key}`}
                    >
                      <RewardCardVisual reward={reward} index={index} popular={popular} />
                      <div className="rc-card__body">
                        <h3>{reward.rewardName || reward.name || `Reward ${index + 1}`}</h3>
                        <p>
                          {reward.description ||
                            reward.rewardDescription ||
                            'Exclusive partner reward from the catalog.'}
                        </p>
                        <span className="rc-card__cost">{formatPts(cost)} Points</span>
                        <button
                          type="button"
                          className={`rc-card__cta${
                            status === 'claimed'
                              ? ' rc-card__cta--claimed'
                              : status === 'available'
                                ? ' rc-card__cta--available'
                                : ' rc-card__cta--locked'
                          }`}
                          disabled={status !== 'available'}
                          aria-disabled={status !== 'available'}
                          onClick={() => {
                            if (status !== 'available') return;
                            setClaimError('');
                            setClaimType('');
                            setSelected(reward);
                          }}
                        >
                          {status === 'claimed' ? (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              Already Claimed
                            </>
                          ) : status === 'available' ? (
                            'Claim Reward'
                          ) : cost > availableToClaim ? (
                            `Need ${formatPts(cost - availableToClaim)} more PTS`
                          ) : (
                            'Locked'
                          )}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            <section className="rc-howto">
              <p>New to rewards? See how earning, choosing, and claiming works in 3 simple steps.</p>
              <Link className="rc-howto__btn" to="/rewards/how-it-works">
                How it Works
                <ArrowRight className="w-4 h-4" />
              </Link>
            </section>
          </>
        )}

        <ClaimRewardModal
          open={Boolean(selected)}
          reward={selected}
          claimType={claimType}
          onClaimTypeChange={setClaimType}
          loading={claimLoading}
          error={claimError}
          onClose={() => {
            if (claimLoading) return;
            setSelected(null);
            setClaimType('');
          }}
          onConfirm={handleConfirmClaim}
        />
      </div>
    </div>
  );
};

export default ClaimRewards;
